import { Gauge, Route, AlertTriangle } from 'lucide-react';
import { LEG_COLORS } from './RouteMap';
import { useTranslation, useI18nStore } from '../../lib/i18n';
const tt = (k, p) => useI18nStore.getState().t(`startStopAdmin.${k}`, p);


const Leg = ({ color, label, km, sub, testId }) => (
  <div className="flex items-center gap-3 py-2" data-testid={testId}>
    <span className="h-8 w-1.5 rounded-full shrink-0" style={{ background: color }} />
    <div className="flex-1 min-w-0">
      <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">{label}</div>
      {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
    </div>
    <div className="text-lg font-black tabular-nums">{km == null ? '—' : `${km} km`}</div>
  </div>
);

const Total = ({ icon: Icon, label, km, formula, strong, testId }) => (
  <div className={`rounded-xl p-3 ${strong ? 'bg-slate-900 text-white' : 'bg-slate-100 dark:bg-slate-800'}`} data-testid={testId}>
    <div className={`text-[11px] flex items-center gap-1 ${strong ? 'text-slate-300' : 'text-slate-500'}`}><Icon className="h-3.5 w-3.5" />{label}</div>
    <div className="text-2xl font-black tabular-nums mt-0.5">{km == null ? '—' : `${km} km`}</div>
    <div className={`text-[10px] mt-1 ${strong ? 'text-slate-400' : 'text-slate-500'}`}>{formula}</div>
  </div>
);

export function DistanceBreakdown({ data }) {
  const by = Object.fromEntries((data.legs || []).map((l) => [l.key, l]));
  const sub = (l) => l && `${tt(l.source === 'road' ? 'road' : 'straight')}${l.minutes != null ? ` · ${tt('aboutMin', { m: l.minutes })}` : ''}`;
  const t = data.totals || {};
  return (
    <div className="space-y-3" data-testid="ss-distance-breakdown">
      {!data.depot && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 text-amber-800 text-xs p-2.5" data-testid="ss-no-depot-loc">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span dangerouslySetInnerHTML={{ __html: tt('noDepotLoc', { depot: String(data.depot_name || tt('depot')).replace(/[<>&]/g, '') }) }} />
        </div>
      )}
      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {data.depot && <Leg color={LEG_COLORS.depot_start} label={tt('legDepotStart')} km={by.depot_start?.km} sub={sub(by.depot_start)} testId="ss-leg-depot_start" />}
        <Leg color={LEG_COLORS.start_stop} label={tt('legOdo')} km={data.odometer_km}
          sub={`${tt('legOdoSub')}${by.start_stop ? ` · ${tt('onMap', { km: by.start_stop.km })}` : ''}`} testId="ss-leg-odometer" />
        {data.depot && <Leg color={LEG_COLORS.stop_depot} label={tt('legStopDepot')} km={by.stop_depot?.km} sub={sub(by.stop_depot)} testId="ss-leg-stop_depot" />}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Total icon={Gauge} label={tt('totOdo')} km={t.odometer_based_km} strong formula={data.depot ? tt('totOdoF') : tt('totOdoOnly')} testId="ss-total-odometer" />
        <Total icon={Route} label={tt('totMap')} km={t.map_km} formula={tt((data.legs || []).length === 1 ? 'totMapF1' : 'totMapF', { n: (data.legs || []).length })} testId="ss-total-map" />
      </div>
    </div>
  );
}
